import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "hiddenInput", "tagButton", "counter", "submit", "search", "tagsContainer" ]
  static values = {
    max: Number // 8
  }

  connect() {
    this.selectedTags = []
    
    // Extrait tous les mots-clés du lexique autorisés directement depuis les boutons de l'UI
    const allowedTerms = this.tagButtonTargets.map(button => button.dataset.term)
    
    // Initialise depuis la valeur existante du champ caché (édition)
    const initialVal = this.hiddenInputTarget.value
    if (initialVal) {
      // Split by spaces or commas, remove leading '#'
      const rawTags = initialVal.split(/[\s,]+/).map(t => t.trim().replace(/^#/, '').replace(/-/g, ' ')).filter(t => t.length > 0)
      
      // Nettoie et mappe les tags vers la casse exacte du lexique si match (insensible à la casse), sinon garde le tag personnalisé
      this.selectedTags = rawTags.map(tag => {
        const match = allowedTerms.find(term => term.toLowerCase() === tag.toLowerCase())
        return match || tag
      }).filter(Boolean)
    }
    
    // Si l'article avait plus de la limite de tags, on tronque
    if (this.selectedTags.length > this.maxValue) {
      this.selectedTags = this.selectedTags.slice(0, this.maxValue)
    }
    
    this.updateHiddenInput()
    this.updateUI()
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.addCustomTag()
    } else if (event.key === ",") {
      event.preventDefault()
      this.addCustomTag()
    }
  }

  addCustomTag() {
    if (!this.hasSearchTarget) return
    
    const value = this.searchTarget.value.trim().replace(/^#/, '')
    if (value.length === 0) return

    // Limite stricte
    if (this.selectedTags.length >= this.maxValue) {
      alert(`Vous ne pouvez pas ajouter plus de ${this.maxValue} mots-clés.`)
      return
    }

    // Vérifie les doublons (insensible à la casse)
    const alreadySelected = this.selectedTags.some(t => t.toLowerCase() === value.toLowerCase())
    if (alreadySelected) {
      this.searchTarget.value = ""
      return
    }

    // Si le mot est dans le lexique, on utilise la casse exacte du lexique
    const allowedTerms = this.tagButtonTargets.map(button => button.dataset.term)
    const match = allowedTerms.find(term => term.toLowerCase() === value.toLowerCase())
    
    const tagToAdd = match || value

    this.selectedTags.push(tagToAdd)
    this.searchTarget.value = ""
    
    this.updateHiddenInput()
    this.updateUI()
  }

  toggle(event) {
    const term = event.currentTarget.dataset.term
    const isSelected = this.selectedTags.includes(term)

    if (isSelected) {
      // Retirer
      this.selectedTags = this.selectedTags.filter(t => t !== term)
    } else {
      // Ajouter si sous la limite
      if (this.selectedTags.length >= this.maxValue) {
        alert(`Vous ne pouvez pas sélectionner plus de ${this.maxValue} mots-clés.`)
        return
      }
      this.selectedTags.push(term)
    }

    this.updateHiddenInput()
    this.updateUI()
  }

  remove(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (index >= 0 && index < this.selectedTags.length) {
      this.selectedTags.splice(index, 1)
      this.updateHiddenInput()
      this.updateUI()
    }
  }

  updateHiddenInput() {
    // Format tags as space-separated hashtags for the DB: #tag-one #tag-two
    this.hiddenInputTarget.value = this.selectedTags.map(tag => {
      const cleanTag = tag.trim().replace(/\s+/g, '-').replace(/^#/, '')
      return `#${cleanTag}`
    }).join(" ")
  }

  updateUI() {
    // 1. Met à jour le style visuel de chaque bouton du lexique (brand colors)
    this.tagButtonTargets.forEach(button => {
      const term = button.dataset.term
      const isSelected = this.selectedTags.includes(term)

      if (isSelected) {
        button.className = "px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all bg-brand-600 text-white border border-brand-600 shadow-sm flex items-center gap-1.5"
        button.querySelector(".status-icon").innerHTML = `
          <svg class="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        `
      } else {
        button.className = "px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-all bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-1.5"
        button.querySelector(".status-icon").innerHTML = `
          <svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        `
      }
    })

    // 2. Affiche les badges des mots-clés sélectionnés
    this.renderTags()

    // 3. Met à jour le compteur à l'écran
    const count = this.selectedTags.length
    this.counterTarget.textContent = `${count} / ${this.maxValue}`
    
    if (count === this.maxValue) {
      this.counterTarget.className = "text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-md border border-green-200"
    } else {
      this.counterTarget.className = "text-sm font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md border border-orange-200"
    }

    // 4. Désactive la saisie si la limite est atteinte
    if (this.hasSearchTarget) {
      if (count >= this.maxValue) {
        this.searchTarget.disabled = true
        this.searchTarget.placeholder = `Limite de ${this.maxValue} mots-clés atteinte`
        this.searchTarget.classList.add("bg-gray-50", "cursor-not-allowed")
      } else {
        this.searchTarget.disabled = false
        this.searchTarget.placeholder = "Saisir un nouveau mot-clé personnalisé (non présent dans la liste)..."
        this.searchTarget.classList.remove("bg-gray-50", "cursor-not-allowed")
      }
    }

    // 5. Active ou désactive le bouton de soumission
    if (this.hasSubmitTarget) {
      const isValid = (count <= this.maxValue)
      this.submitTarget.disabled = !isValid
      if (isValid) {
        this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
      } else {
        this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
      }
    }
  }

  renderTags() {
    if (!this.hasTagsContainerTarget) return

    if (this.selectedTags.length === 0) {
      this.tagsContainerTarget.innerHTML = `<span class="text-sm text-gray-400 italic">Aucun mot-clé sélectionné</span>`
      return
    }

    this.tagsContainerTarget.innerHTML = this.selectedTags.map((tag, index) => {
      return `
        <span class="inline-flex items-center gap-1.5 bg-brand-50 text-brand-700 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-sm">
          #${tag.replace(/\s+/g, '-').toLowerCase()}
          <button type="button" data-action="click->autocomplete-tags#remove" data-index="${index}" class="text-brand-600/60 hover:text-brand-700 transition-colors focus:outline-none ml-1">
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      `
    }).join("")
  }
}
